/**
 * ActivationService
 *
 * Handles activation-related API operations including status updates
 * (normal, test, non-serviced) for call quality management.
 */

import { api } from 'src/boot/axios'

export interface ActivationUpdatePayload {
  activationID: number
  caseNumber: number
  isTest?: boolean
  status?: number
  nonServicedReason?: string
}

export default class ActivationService {
  /**
   * Update activation status (normal, test, or non-serviced)
   *
   * @param payload - Activation update data
   * @returns Promise that resolves when update is complete
   */
  async updateActivationStatus(payload: ActivationUpdatePayload): Promise<void> {
    await api.put(`api/v1/activations/${payload.activationID}`, payload)
  }

  /**
   * Mark activation as normal (serviced successfully)
   * Clears test flag and non-serviced reason
   *
   * @param activationID - ID of activation to update
   * @param caseNumber - Case number for case refresh
   */
  async markAsNormal(activationID: number, caseNumber: number): Promise<void> {
    return this.updateActivationStatus({
      activationID,
      caseNumber,
      isTest: false,
      status: 0
    })
  }

  /**
   * Mark activation as test call
   * Sets test flag, used for training/QA purposes
   *
   * @param activationID - ID of activation to update
   * @param caseNumber - Case number for case refresh
   */
  async markAsTest(activationID: number, caseNumber: number): Promise<void> {
    return this.updateActivationStatus({
      activationID,
      caseNumber,
      isTest: true
    })
  }

  /**
   * Mark activation as non-serviced with reason
   * Records why the call was not completed successfully
   *
   * @param activationID - ID of activation to update
   * @param caseNumber - Case number for case refresh
   * @param reason - Reason why call was non-serviced
   */
  async markAsNonServiced(
    activationID: number,
    caseNumber: number,
    reason: string
  ): Promise<void> {
    return this.updateActivationStatus({
      activationID,
      caseNumber,
      nonServicedReason: reason
    })
  }
}
